import React, { useEffect, useState } from "react";
import {
  IonList,
  IonItem,
  IonGrid,
  IonRow,
  IonCol,
  IonLabel,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonIcon,
  IonNote,
  IonChip,
  IonSpinner,
} from "@ionic/react";
import {
  createOutline,
  pauseOutline,
  copyOutline,
  closeCircleOutline,
  arrowBackCircleOutline,
  refreshOutline,
} from "ionicons/icons";
import { Alert } from "./notifications";

interface AlertsTableProps {
  alerts: Alert[];
  isLoading?: boolean;
  handleViewDetails: (alert: Alert) => void;
  handleEditAlert: (alert: Alert) => void;
  handleCopyAlert: (alert: Alert) => void;
  handlePauseAlert: (alert: Alert) => void;
  handleResumeAlert: (alert: Alert) => void;
  handleRetryAlert: (alert: Alert) => void;
  setDeleteAlertConfirmationOpen: (isOpen: boolean) => void;
  setAlertToBeDeleted: (alert: Alert | null) => void;
  slidingItemRefs: React.MutableRefObject<{
    [key: string]: HTMLIonItemSlidingElement | null;
  }>;
}

const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  isLoading = false,
  handleViewDetails,
  handleCopyAlert,
  handleEditAlert,
  handlePauseAlert,
  handleResumeAlert,
  handleRetryAlert,
  setDeleteAlertConfirmationOpen,
  setAlertToBeDeleted,
  slidingItemRefs,
}) => {
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>(
    () => {
      try {
        const savedFilters = sessionStorage.getItem("alert_status_filters");
        return savedFilters ? JSON.parse(savedFilters) : ["ALL"];
      } catch (e) {
        return ["ALL"];
      }
    },
  );

  useEffect(() => {
    sessionStorage.setItem(
      "alert_status_filters",
      JSON.stringify(selectedStatusFilters),
    );
  }, [selectedStatusFilters]);

  const handleStatusFilterChange = React.useCallback((value: string) => {
    setSelectedStatusFilters([value]);
  }, []);

  const filterChips = React.useMemo(
    () => (
      <div className="ob__filterchips_wrapper" style={{ marginTop: 0 }}>
        {["ALL", "ACTIVE", "TRIGGERED", "PAUSED", "EXPIRED"].map((status) => (
          <div key={status}>
            <IonChip
              outline={!selectedStatusFilters.includes(status)}
              onClick={() => handleStatusFilterChange(status)}
            >
              {status.charAt(0) + status.slice(1).toLowerCase()}
            </IonChip>
          </div>
        ))}
      </div>
    ),
    [selectedStatusFilters, handleStatusFilterChange],
  );

  const RenderMobileHeader = () => {
    return (
      <div>
        <IonItem className="even_items_bg ion-no-padding" lines="none">
          <IonGrid>
            <IonRow style={{ fontSize: "0.8em" }}>
              <IonCol size="6">
                <b>NAME</b>
              </IonCol>
              <IonCol size="3">
                <b>STATUS</b>
              </IonCol>
              <IonCol size="3">
                <b>LAST UPDATED</b>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonItem>
      </div>
    );
  };

  const filteredAlerts = alerts
    .filter((alert) => alert.status !== "DELETED")
    .filter((alert) => {
      if (selectedStatusFilters.includes("ALL")) {
        return true;
      }
      if (
        selectedStatusFilters.includes("ACTIVE") &&
        alert.status === "PENDING"
      ) {
        return true;
      }
      return selectedStatusFilters.includes(alert.status);
    })
    .sort((a, b) => {
      const aTime =
        Number(a.modifiedAt) < 1e11
          ? Number(a.modifiedAt) * 1000
          : Number(a.modifiedAt);
      const bTime =
        Number(b.modifiedAt) < 1e11
          ? Number(b.modifiedAt) * 1000
          : Number(b.modifiedAt);
      return bTime - aTime;
    });

  return (
    <>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "var(--ion-background-color)",
          display: "flex",
          flexDirection: "column",
          paddingTop: "12px",
        }}
      >
        {filterChips}
        <RenderMobileHeader />
      </div>
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "40px",
            flexDirection: "column",
          }}
        >
          <IonSpinner />
        </div>
      ) : filteredAlerts.length > 0 ? (
        <>
          {/* <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {tableHeaders.map((elem: string, id: number) => (
              <div
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  backgroundColor: "#112B4A",
                  width: "calc(100% / 3)",
                  justifyContent:
                    id === 1 || id === 3 ? "flex-end" : "flex-start",
                }}
              >
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "11px" }}
                >
                  {elem}
                </p>
              </div>
            ))}
          </div> */}
          <IonList className="ion-no-padding">
            {filteredAlerts.map((alert, index) => (
              <IonItemSliding
                ref={(el) => (slidingItemRefs.current[alert.reqId] = el)}
                key={alert?.reqId}
              >
                <IonItem
                  className={
                    index % 2 === 0
                      ? "even_item_bg ion-no-padding"
                      : "ion-no-padding"
                  }
                  lines="none"
                >
                  <IonGrid>
                    <IonRow
                      style={{ fontSize: "0.8em", alignItems: "flex-start" }}
                    >
                      <IonCol
                        size="6"
                        style={{ minWidth: 0 }}
                        onClick={() => handleViewDetails(alert)}
                      >
                        <div
                          style={{
                            wordBreak: "break-word",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                            width: "100%",
                          }}
                        >
                          {alert?.alertName?.replaceAll("_", " ")}
                        </div>
                      </IonCol>
                      <IonCol size="3">
                        <IonLabel
                          color={
                            alert?.status === "ACTIVE"
                              ? "success"
                              : alert?.status === "TRIGGERED"
                                ? "primary"
                                : alert?.status === "PAUSED"
                                  ? "medium"
                                  : alert?.status === "PENDING"
                                    ? "dark"
                                    : alert?.status === "EXPIRED" ||
                                        alert?.status === "FAILED"
                                      ? "danger"
                                      : "medium"
                          }
                        >
                          {alert?.status ?? "INACTIVE"}
                        </IonLabel>
                      </IonCol>
                      <IonCol size="3">
                        <IonLabel>
                          {new Date(
                            Number(alert?.modifiedAt) < 1e11
                              ? Number(alert?.modifiedAt) * 1000
                              : Number(alert?.modifiedAt),
                          ).toLocaleString()}
                        </IonLabel>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonItem>
                <IonItemOptions side="end">
                  {alert?.status === "FAILED" && (
                    <IonItemOption
                      color="warning"
                      onClick={() => handleRetryAlert(alert)}
                    >
                      <IonIcon slot="icon-only" icon={refreshOutline} />
                    </IonItemOption>
                  )}
                  {alert?.status === "ACTIVE" && (
                    <IonItemOption
                      color="medium"
                      onClick={() => handlePauseAlert(alert)}
                    >
                      <IonIcon slot="icon-only" icon={pauseOutline} />
                    </IonItemOption>
                  )}
                  {alert?.status === "PAUSED" && (
                    <IonItemOption
                      color="success"
                      onClick={() => handleResumeAlert(alert)}
                    >
                      <IonIcon slot="icon-only" icon={arrowBackCircleOutline} />
                    </IonItemOption>
                  )}
                  {(alert?.status === "ACTIVE" ||
                    alert?.status === "PAUSED") && (
                    <IonItemOption onClick={() => handleEditAlert(alert)}>
                      <IonIcon slot="icon-only" icon={createOutline} />
                    </IonItemOption>
                  )}
                  {alert?.status !== "EXPIRED" && (
                    <IonItemOption
                      color="secondary"
                      onClick={() => handleCopyAlert(alert)}
                    >
                      <IonIcon slot="icon-only" icon={copyOutline} />
                    </IonItemOption>
                  )}
                  <IonItemOption
                    color="danger"
                    onClick={() => {
                      setDeleteAlertConfirmationOpen(true);
                      setAlertToBeDeleted(alert);
                    }}
                  >
                    <IonIcon slot="icon-only" icon={closeCircleOutline} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            ))}
          </IonList>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "40px",
            flexDirection: "column",
          }}
        >
          <IonNote
            color="medium"
            style={{ fontSize: "var(--one-font-size-10)" }}
          >
            {alerts.length === 0
              ? "No alerts to be displayed."
              : "No alerts found for the selected filter."}
          </IonNote>
        </div>
      )}
    </>
  );
};

export default AlertsTable;
